import { TelemetryService } from "../telemetry.service";

/**
 * tracks a user session, sending telemetry events for each step in the process.
 * the general flow looks like:
 * 1. Open (optional) - user accessed the feature
 *  2a. Closed (no telemetry event) - user left without engaging
 *  2b. Engage - user interacted with the feature
 *   3a. Complete - user successfully finished the session workflow
 *   3b. Abandon - user engaged, then left without finishing the workflow
 */
export class TelemetrySession {
    private static Counter = 1;

    /**
     * Not globally unique; only unique within a user's tab
     */
    readonly id = TelemetrySession.Counter++;
    #status: TelemetrySession.Status = "unstarted";

    get status() {
        return this.#status;
    }

    constructor(
        private readonly category: string,
        private readonly subCategory?: string,
    ) {}

    /**
     * opening means the user opted in to a feature, e.g. opened the AI sidebar
     */
    open() {
        if (this.#status === "unstarted") {
            void TelemetryService.sendWithSub(this.category, this.subCategory, "Open");
            this.#status = "opened";
        }
    }

    /**
     * engaging indicates that the user did more than just look at the feature.
     * it could mean they started typing, clicked around, etc.
     */
    engage() {
        if (["opened", "unstarted"].includes(this.#status)) {
            void TelemetryService.sendWithSub(this.category, this.subCategory, "Engage");
            this.#status = "engaged";
        }
    }

    /**
     * completion indicates that the user has completed a workflow using the feature.
     * if the user has not engaged, an `Engage` event will be triggered.
     */
    complete() {
        this.engage();
        if (this.#status === "engaged") {
            void TelemetryService.sendWithSub(this.category, this.subCategory, "Complete");
            this.#status = "completed";
        }
    }

    /**
     * close the session, triggering an Abandon event if the user had engaged without completing
     */
    close() {
        if (this.#status === "engaged") {
            void TelemetryService.sendWithSub(this.category, this.subCategory, "Abandon");
            this.#status = "abandoned";
        } else {
            this.#status = "closed";
        }
    }
}

export namespace TelemetrySession {
    export type LifecycleEvent = "Open" | "Engage" | "Complete" | "Abandon";
    export type Status = "unstarted" | "opened" | "engaged" | "completed" | "abandoned" | "closed";
}
